import React from 'react';
import { PlainNavigationItem, RouterAdapter } from '@mds/app-layout';
import {
    Link as RouterLink,
    matchPath,
    NavLink as RouterNavLink,
    useLocation,
} from 'react-router-dom';

type NavigationItemProps = React.ComponentProps<typeof PlainNavigationItem> & {
    to?: string;
    exact?: boolean;
    strict?: boolean;
    sensitive?: boolean;
};

type LinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    to?: string;
};

const omitNavigationDomProps = ({
    fullWidth,
    indicator,
    selectionFollowsFocus,
    textColor,
    ...rest
}: any) => rest;

const RouterNavLinkComponent = React.forwardRef<HTMLAnchorElement, any>(
    (props, ref) => (
        <RouterNavLink {...omitNavigationDomProps(props)} innerRef={ref} />
    )
);

RouterNavLinkComponent.displayName = 'RouterNavLinkComponent';

const RouterLinkComponent = React.forwardRef<HTMLAnchorElement, any>(
    (props, ref) => (
        <RouterLink {...omitNavigationDomProps(props)} innerRef={ref} />
    )
);

RouterLinkComponent.displayName = 'RouterLinkComponent';

const NavigationItemComponent = ({
    to,
    exact,
    strict,
    sensitive,
    active,
    ...rest
}: NavigationItemProps) => {
    const location = useLocation();
    const match = to
        ? matchPath(location.pathname, {
              path: to,
              exact,
              strict,
              sensitive,
          })
        : null;

    if (!to) {
        return <PlainNavigationItem active={active} {...rest} />;
    }

    return (
        <PlainNavigationItem
            {...rest}
            active={active ?? !!match}
            component={RouterNavLinkComponent}
            to={to}
            exact={exact}
            strict={strict}
            sensitive={sensitive}
        />
    );
};

const LinkComponent = ({ href, to, children, ...rest }: LinkProps) => {
    if (href) {
        return (
            <a href={href} {...omitNavigationDomProps(rest)}>
                {children}
            </a>
        );
    }

    return (
        <RouterLinkComponent to={to ?? '/'} {...rest}>
            {children}
        </RouterLinkComponent>
    );
};

export const reactRouterV5Adapter: RouterAdapter = {
    NavigationItemComponent:
        NavigationItemComponent as RouterAdapter['NavigationItemComponent'],
    LinkComponent: LinkComponent as RouterAdapter['LinkComponent'],
    homePageLinkProps: { to: '/' } as RouterAdapter['homePageLinkProps'],
};
